// Built-in modules
import moment from "moment/moment";

// Third-party libraries
import React, { useEffect, useState } from "react";
import { Link as DomLink } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useMediaQueryUtils from "../../utils/mediaQueryUtils";
import formatting from "../../utils/formatHelpers";
import {
  Stack,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  Select,
  MenuItem,
  TextField,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Skeleton,
  Grid,
  Box,
} from "@mui/material";

// Local modules
import { storage } from "../../utils";
import { api } from "../../utils";

export default function UserProfile() {
  const isMdScreen = useMediaQueryUtils("md");
  const newDate = new Date();
  const [startDate, setStartDate] = useState(
    new Date(newDate.setMonth(newDate.getMonth() - 3))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [status, setStatus] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [reload, setReload] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [rows, setRows] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [historyLoading, setHistoryLoading] = useState(true);
  const currentUser = JSON.parse(storage.getStorageItem("currentUser"));
  const [paginatedRows, setPaginatedRows] = useState([]);
  const [scrollToTop, setScrollToTop] = useState(false);

  const DateRangeField = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <Stack sx={{ flex: 1, minWidth: "120px" }}>
          <Typography sx={{ fontWeight: 700, paddingBottom: ".3rem" }}>
            Start Date
          </Typography>
          <Box
            sx={{
              border: "1px solid #939598",
              p: 0.5,
              width: "100%",
              "& input": {
                width: "100%",
                border: "none",
                outline: "none",
              },
            }}
          >
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              id="start-date"
            />
          </Box>
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Stack sx={{ flex: 1, minWidth: "120px" }}>
          <Typography sx={{ fontWeight: 700, paddingBottom: ".3rem" }}>
            End Date
          </Typography>
          <Box
            sx={{
              border: "1px solid #939598",
              p: 0.5,
              width: "100%",
              "& input": {
                width: "100%",
                border: "none",
                outline: "none",
              },
            }}
          >
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              id="end-date"
            />
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );

  const StatusField = () => (
    <Stack>
      <Typography sx={{ fontWeight: "700", paddingBottom: ".3rem" }}>
        Status
      </Typography>
      <Select
        value={status}
        size="small"
        onChange={(e) => setStatus(e.target.value)}
        MenuProps={{
          PaperProps: {
            sx: {
              border: "1px solid #939598",
              borderRadius: "0",
            },
          },
        }}
        fullWidth
      >
        <MenuItem key="all" value="All">
          All
        </MenuItem>
        <MenuItem key="pending" value="Pending">
          Pending
        </MenuItem>
        <MenuItem key="shipped" value="Shipped">
          Shipped
        </MenuItem>
      </Select>
    </Stack>
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setPaginatedRows(
      invoices.slice(newPage * rowsPerPage, newPage * rowsPerPage + rowsPerPage)
    );
    setScrollToTop(true);
  };

  const handleChangeRowsPerPage = (event) => {
    let rows = parseInt(event.target.value, 10);
    setRowsPerPage(rows);
    setPage(0);
    setPaginatedRows(invoices.slice(0, rows));
    setScrollToTop(true);
  };

  const handleResetButton = () => {
    setStatus("All");
    setEndDate(new Date());
    setStartDate(new Date(newDate.setMonth(newDate.getMonth() - 3)));
    setSearchTerm("");
    setReload(true);
  };

  const handleChange = (e) => {
    setSearchTerm(e.target.value);
  };

  function search() {
    setHistoryLoading(true);
    const query = {
      date_start: moment(startDate).format("MM/DD/YYYY"),
      date_end: moment(endDate).format("MM/DD/YYYY"),
      status: status,
      q: searchTerm,
    };
    api.getHistory(query).then((response) => {
      setInvoices(response.data.results);
      setHistoryLoading(false);
      setReload(true);
      setPaginatedRows(
        invoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      );
    });
  }

  useEffect(() => {
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setScrollToTop(false);
    }
  }, [scrollToTop]);

  useEffect(() => {
    let isMounted = true;
    setHistoryLoading(true);
    const query = {
      date_start: moment(startDate).format("MM/DD/YYYY"),
      date_end: moment(endDate).format("MM/DD/YYYY"),
      status: status,
      q: searchTerm,
    };
    api.getHistory(query).then((response) => {
      if (isMounted) {
        if (response && response.data) {
          setInvoices(response.data);
          setReload(false);
          setRows(response.data.length);
          setPaginatedRows(
            response.data.slice(
              page * rowsPerPage,
              page * rowsPerPage + rowsPerPage
            )
          );
        }
        setHistoryLoading(false);
      }
    });
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  return (
    <>
      <Stack direction={isMdScreen ? "column" : "row"} spacing={2}>
        <Card
          variant="outlined"
          sx={{
            width: isMdScreen ? "100%" : "25%",
          }}
        >
          <CardContent>
            {currentUser !== null ? (
              <Stack spacing={5} alignItems="center">
                <Typography variant="h6">{`Hello ${currentUser.FirstName} ${currentUser.LastName}!`}</Typography>
                <Typography>{`Email: ${currentUser.Email}`}</Typography>
                <Typography>{`Phone #: ${currentUser.PhoneNumber}`}</Typography>
                <DomLink
                  to={{
                    pathname: "/update-password",
                  }}
                >
                  <Button variant="contained">Update Password</Button>
                </DomLink>
              </Stack>
            ) : (
              <Skeleton variant="text" width="10rem" />
            )}
          </CardContent>
        </Card>
        <Stack sx={{ width: isMdScreen ? "100%" : "75%" }} spacing={3}>
          <Card variant="outlined">
            <CardHeader
              sx={{ backgroundColor: "#cfdde9" }}
              title={
                <Typography variant="accordianTitle">
                  Default Addresses
                </Typography>
              }
            />
            <CardContent>
              <Stack direction="row" spacing={8}>
                <Stack spacing={2}>
                  <Typography variant="navText">Billing Address:</Typography>
                  {currentUser !== null ? (
                    <Stack>
                      <Typography>{currentUser.BillTo.Street1}</Typography>
                      <Typography>{currentUser.BillTo.Street2}</Typography>
                      <Typography>
                        {currentUser.BillTo.City}, {currentUser.BillTo.State}{" "}
                        {currentUser.BillTo.ZipCode}
                      </Typography>
                    </Stack>
                  ) : (
                    <Skeleton variant="text" width="10rem" />
                  )}
                </Stack>
                <Stack spacing={2}>
                  <Typography variant="navText">Shipping Address:</Typography>
                  {currentUser !== null ? (
                    <Stack>
                      <Typography>{currentUser.MainShipTo.Street1}</Typography>
                      <Typography>{currentUser.MainShipTo.Street2}</Typography>
                      <Typography>
                        {currentUser.MainShipTo.City},{" "}
                        {currentUser.MainShipTo.State}{" "}
                        {currentUser.MainShipTo.ZipCode}
                      </Typography>
                    </Stack>
                  ) : (
                    <Skeleton variant="text" width="10rem" />
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Stack>
      <Card variant="outlined" sx={{ marginTop: "2rem" }}>
        <CardHeader
          sx={{ backgroundColor: "#cfdde9" }}
          title={
            <Typography variant="accordianTitle">Order History</Typography>
          }
        />
        <CardContent>
          <Box sx={{ maxWidth: "1200px", margin: "0 auto", width: "100%" }}>
            <Grid container spacing={2}>
              <Grid item lg={3}>
                <DateRangeField />
              </Grid>
              <Grid item lg={3}>
                <StatusField />
              </Grid>
              <Grid item lg={3}>
                <Stack>
                  <div>
                    <Typography
                      sx={{ fontWeight: "700", paddingBottom: ".3rem" }}
                    >
                      Search PO/Invoice
                    </Typography>
                  </div>
                  <TextField
                    color="primary"
                    onChange={handleChange}
                    size="small"
                    sx={{
                      width: "100%",
                      margin: "unset",
                    }}
                    value={searchTerm}
                  />
                </Stack>
              </Grid>
              <Grid
                item
                lg={1.5}
                sx={{
                  display: "flex",
                  alignContent: "flex-end",
                  paddingBottom: "2px",
                }}
              >
                <Button
                  onClick={() => search()}
                  variant="contained"
                  sx={{ marginTop: "2rem" }}
                >
                  Search
                </Button>
              </Grid>
              <Grid
                item
                lg={1.5}
                sx={{
                  display: "flex",
                  alignContent: "flex-end",
                  paddingBottom: "2px",
                }}
              >
                <Button
                  variant="outlined"
                  color="error"
                  sx={{ marginTop: "2rem" }}
                  onClick={() => handleResetButton()}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </Box>
          <Divider flexItem sx={{ marginTop: "1rem", marginBottom: "1rem" }} />
          {historyLoading ? (
            <Stack>
              <Skeleton variant="text" sx={{ fontSize: "5rem" }} />
              <Skeleton variant="text" sx={{ fontSize: "5rem" }} />
              <Skeleton variant="text" sx={{ fontSize: "5rem" }} />
              <Skeleton variant="text" sx={{ fontSize: "5rem" }} />
            </Stack>
          ) : (
            <>
              {invoices && invoices.length > 0 ? (
                <>
                  <TableContainer component={Paper}>
                    <Table aria-label="collapsible table">
                      <TableHead>
                        <TableRow>
                          <TableCell align="right">
                            <Typography>Date Ordered</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>Status</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>Description</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>SKU</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>Material</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>Quantity</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>Weight</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>Treatment</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>Special Instructions</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>Invoice #</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>PO #</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>Total</Typography>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedRows.map((row, id) => (
                          <TableRow key={id}>
                            <TableCell align="right">
                              {moment(row.order_date).format("MM/DD/YYYY")}
                            </TableCell>
                            <TableCell align="right">{row.status}</TableCell>
                            <TableCell align="right">
                              {row.specifications}
                            </TableCell>
                            <TableCell align="right">{row.style}</TableCell>
                            <TableCell align="right">{row.material}</TableCell>
                            <TableCell align="right">
                              {row.pieces > 1 ? `${row.pieces}-` : null}
                              {row.quantity} {row.sales_unit}
                            </TableCell>
                            <TableCell align="right">
                              {row.estimated_weight} {row.estimated_weight_uom}
                            </TableCell>
                            <TableCell align="right">{row.treatment}</TableCell>
                            <TableCell align="right">
                              {row.special_instructions}
                            </TableCell>
                            <TableCell align="right">
                              {row.invoice_id}
                            </TableCell>
                            <TableCell align="right">{row.po_number}</TableCell>
                            <TableCell align="right">
                              {formatting["price2"](row.line_total || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={rows}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[25, 50, 100]}
                  />
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    minHeight: "100px",
                  }}
                >
                  <Typography>
                    No invoices found. Try adjusting your filters and search
                    again.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}
